import { CheckIcon, ChevronRight, MinusIcon, Square } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Badge } from '@/components/ui/badge';
import { CommandGroup, CommandItem, CommandSeparator } from '@/components/ui/command';
import { cn } from '@/lib/tailwind-utils';
import { FilterDropdownBase } from './table-filter-dropdown-base';
import {
  getGroupedAvailableValues,
  getGroupedBulkSelectState,
  nextGroupedBulkSelectValues,
  shouldShowBulkSelectAction,
  TableFilterBulkSelectState,
} from './table-filter-bulk-select';

interface CheckboxIconProps {
  isSelected: boolean;
  isIndeterminate?: boolean;
}

const CheckboxIcon = ({ isSelected, isIndeterminate = false }: CheckboxIconProps) => (
  <div
    className={cn(
      'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
      isSelected || isIndeterminate ? 'bg-primary text-primary-foreground' : 'opacity-50 [&_svg]:invisible',
    )}
  >
    {isIndeterminate ? <Square className="h-3 w-3 pr-[0.8px]" /> : <CheckIcon className="h-4 w-4" />}
  </div>
);

const MultiSelectCheckboxIcon = ({ state }: { state: TableFilterBulkSelectState }) => {
  const isActive = state !== 'none';

  return (
    <div
      className={cn(
        'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
        isActive ? 'bg-primary text-primary-foreground' : 'opacity-50',
      )}
    >
      {state === 'partial' ? (
        <MinusIcon className="h-3 w-3" />
      ) : (
        <CheckIcon className={cn('h-4 w-4', state !== 'all' && 'invisible')} />
      )}
    </div>
  );
};

interface SubmenuPortalProps<TChildValue> {
  rect: DOMRect;
  children: TValueOption<TChildValue>[];
  selectedSubValues: TChildValue[];
  subValueToString: (value: TChildValue) => string;
  onToggleChild: (value: TChildValue) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

function SubmenuPortal<TChildValue>({
  rect,
  children,
  selectedSubValues,
  subValueToString,
  onToggleChild,
  onMouseEnter,
  onMouseLeave,
}: SubmenuPortalProps<TChildValue>) {
  return createPortal(
    <div
      style={{ top: rect.top, left: rect.right + 4 }}
      className="fixed z-50 min-w-[200px] rounded-md border bg-popover p-1 shadow-md"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {children.map((child) => {
        const isSelected = selectedSubValues.some((val) => subValueToString(val) === subValueToString(child.value));
        return (
          <div
            key={subValueToString(child.value)}
            className="flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
            onClick={() => onToggleChild(child.value)}
          >
            <CheckboxIcon isSelected={isSelected} />
            <span>{child.label}</span>
          </div>
        );
      })}
    </div>,
    document.body,
  );
}

type TValueOption<TValue> = {
  label: string;
  value: TValue;
};

export type GroupedOption<TValue, TChildValue = TValue> = {
  label: string;
  value: TValue;
  children?: TValueOption<TChildValue>[];
};

interface TableFilterDropdownSubItemsProps<TValue, TChildValue> {
  title?: string;
  options: GroupedOption<TValue, TChildValue>[];
  selectedValues: TValue[];
  selectedSubValues: TChildValue[];
  onChange: (values: TValue[]) => void;
  onSubValuesChange: (values: TChildValue[]) => void;
  isLoading?: boolean;
  valueToString?: (value: TValue) => string;
  subValueToString?: (value: TChildValue) => string;
  'data-testid'?: string;
}

export function TableFilterDropdownSubItems<TValue, TChildValue>({
  title,
  options,
  selectedValues,
  selectedSubValues,
  onChange,
  onSubValuesChange,
  isLoading = false,
  valueToString = (value) => String(value),
  subValueToString = (value) => String(value),
  'data-testid': dataTestId,
}: TableFilterDropdownSubItemsProps<TValue, TChildValue>) {
  const [search, setSearch] = useState('');
  const [expandedValue, setExpandedValue] = useState<TValue | null>(null);
  const [submenuRect, setSubmenuRect] = useState<DOMRect | null>(null);

  const itemRefs = useRef<Record<string, HTMLDivElement>>({});
  const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isOverSubmenu = useRef(false);

  const isSearching = search.trim().length > 0;
  const searchLower = search.toLowerCase();
  const totalSelected = selectedValues.length + selectedSubValues.length;

  useEffect(() => {
    if (isSearching) {
      setExpandedValue(null);
      setSubmenuRect(null);
    }
  }, [isSearching]);

  useEffect(() => {
    return () => {
      if (closeTimeout.current) {
        clearTimeout(closeTimeout.current);
      }
    };
  }, []);

  // Submenu open/close handlers. 100ms delay avoids closing when moving mouse between menu and submenu.
  const openSubmenu = useCallback(
    (value: TValue) => {
      if (closeTimeout.current) {
        clearTimeout(closeTimeout.current);
      }

      const el = itemRefs.current[valueToString(value)];

      if (el) {
        setSubmenuRect(el.getBoundingClientRect());
        setExpandedValue(value);
      }
    },
    [valueToString],
  );

  const scheduleClose = useCallback(() => {
    closeTimeout.current = setTimeout(() => {
      setExpandedValue(null);
      setSubmenuRect(null);
    }, 100);
  }, []);

  const cancelClose = useCallback(() => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
    }
  }, []);

  const handleSubmenuMouseEnter = useCallback(() => {
    isOverSubmenu.current = true;
    cancelClose();
  }, [cancelClose]);

  const handleSubmenuMouseLeave = useCallback(() => {
    isOverSubmenu.current = false;
    scheduleClose();
  }, [scheduleClose]);

  // Toggling a parent adds/removes all its children.
  // Toggling a child only adds/removes that child, but also unchecks the parent if it was selected.
  const toggleParent = useCallback(
    (option: GroupedOption<TValue, TChildValue>) => {
      const isExpanded = expandedValue !== null && valueToString(expandedValue) === valueToString(option.value);

      if (!isExpanded) {
        setExpandedValue(null);
        setSubmenuRect(null);
      }

      const children = option.children ?? [];
      const isExplicitlySelected = selectedValues.some((val) => valueToString(val) === valueToString(option.value));
      const selectedChildCount = children.filter(({ value }) =>
        selectedSubValues.some((subVal) => subValueToString(subVal) === subValueToString(value)),
      ).length;
      const isCheckedOrIndeterminate = isExplicitlySelected || selectedChildCount > 0;

      if (isCheckedOrIndeterminate) {
        onChange(selectedValues.filter((val) => valueToString(val) !== valueToString(option.value)));

        if (children.length > 0) {
          const childKeys = new Set(children.map(({ value }) => subValueToString(value)));
          onSubValuesChange(selectedSubValues.filter((subVal) => !childKeys.has(subValueToString(subVal))));
        }
      } else {
        onChange([...selectedValues, option.value]);

        if (children.length > 0) {
          const childKeys = new Set(selectedSubValues.map((subVal) => subValueToString(subVal)));
          const newChildren = children
            .filter(({ value }) => !childKeys.has(subValueToString(value)))
            .map(({ value }) => value);

          onSubValuesChange([...selectedSubValues, ...newChildren]);
        }
      }
    },
    [expandedValue, selectedValues, selectedSubValues, onChange, onSubValuesChange, valueToString, subValueToString],
  );

  const toggleChild = useCallback(
    (childValue: TChildValue) => {
      const key = subValueToString(childValue);
      const isRemoving = selectedSubValues.some((subVal) => subValueToString(subVal) === key);

      if (isRemoving) {
        onSubValuesChange(selectedSubValues.filter((subVal) => subValueToString(subVal) !== key));

        // Remove the parent from selectedValues so the backend filters by sub-statuses,
        // not by parent status (which would return all coaches with that status).
        const parentOption = options.find((opt) =>
          (opt.children ?? []).some(({ value }) => subValueToString(value) === key),
        );

        if (parentOption) {
          const parentKey = valueToString(parentOption.value);
          onChange(selectedValues.filter((val) => valueToString(val) !== parentKey));
        }
      } else {
        onSubValuesChange([...selectedSubValues, childValue]);
      }
    },
    [options, selectedValues, selectedSubValues, onChange, onSubValuesChange, valueToString, subValueToString],
  );

  const hasSearchResults = useMemo(
    () =>
      !isSearching ||
      options.some(({ label, children }) => {
        const parentMatches = label.toLowerCase().includes(searchLower);
        const childMatches = (children ?? []).some((child) => child.label.toLowerCase().includes(searchLower));

        return parentMatches || childMatches;
      }),
    [isSearching, options, searchLower],
  );

  const allSelectedLabels = useMemo(
    () => [
      ...selectedValues.map(
        (val) => options.find(({ value }) => valueToString(value) === valueToString(val))?.label ?? valueToString(val),
      ),
      ...selectedSubValues.map((subVal) => {
        for (const opt of options) {
          const child = opt.children?.find(({ value }) => subValueToString(value) === subValueToString(subVal));

          if (child) {
            return child.label;
          }
        }

        return subValueToString(subVal);
      }),
    ],
    [selectedValues, selectedSubValues, options, valueToString, subValueToString],
  );

  // Closing the popover resets the expanded parent and search state.
  const onOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setExpandedValue(null);
      setSubmenuRect(null);
    }
  }, []);

  const onInteractOutside = useCallback((e: Event) => {
    if (isOverSubmenu.current) {
      e.preventDefault();
    }
  }, []);

  const expandedOption = useMemo(
    () =>
      expandedValue !== null
        ? options.find(({ value }) => valueToString(value) === valueToString(expandedValue))
        : null,
    [expandedValue, options, valueToString],
  );

  const handleClearFilters = useCallback(() => {
    onChange([]);
    onSubValuesChange([]);
  }, [onChange, onSubValuesChange]);

  const { parentValues: availableParentValues, childValues: availableChildValues } = useMemo(
    () => getGroupedAvailableValues({ options, search }),
    [options, search],
  );
  const bulkSelectState = getGroupedBulkSelectState({
    availableParentValues,
    availableChildValues,
    selectedParentValues: selectedValues,
    selectedChildValues: selectedSubValues,
  });
  const showBulkSelect = shouldShowBulkSelectAction({
    availableOptionCount: availableParentValues.length + availableChildValues.length,
  });

  const headerSlot = showBulkSelect ? (
    <>
      <CommandGroup forceMount>
        <CommandItem
          forceMount
          value="__bulk-select__"
          onSelect={() => {
            const next = nextGroupedBulkSelectValues({
              availableParentValues,
              availableChildValues,
              selectedParentValues: selectedValues,
              selectedChildValues: selectedSubValues,
            });
            onChange(next.parentValues);
            onSubValuesChange(next.childValues);
          }}
          data-testid="table-filter-bulk-select"
        >
          <MultiSelectCheckboxIcon state={bulkSelectState} />
          <span>{bulkSelectState === 'all' ? 'Deselect all' : 'Select all'}</span>
        </CommandItem>
      </CommandGroup>
      <CommandSeparator />
    </>
  ) : null;

  return (
    <>
      <FilterDropdownBase
        title={title}
        totalSelected={totalSelected}
        selectedLabels={allSelectedLabels}
        isLoading={isLoading}
        search={search}
        onSearchChange={setSearch}
        shouldFilter={false}
        onClearFilters={totalSelected > 0 && hasSearchResults ? handleClearFilters : undefined}
        onOpenChange={onOpenChange}
        onInteractOutside={onInteractOutside}
        onFocusOutside={onInteractOutside}
        headerSlot={headerSlot}
        data-testid={dataTestId}
      >
        {isSearching && !hasSearchResults && <div className="py-6 text-center text-sm">No results found.</div>}
        <CommandGroup>
          {options.map((option) => {
            const children = option.children ?? [];
            const hasChildren = children.length > 0;
            const key = valueToString(option.value);

            if (isSearching) {
              const parentMatches = option.label.toLowerCase().includes(searchLower);
              const matchingChildren = children.filter((c) => c.label.toLowerCase().includes(searchLower));

              if (!parentMatches && matchingChildren.length === 0) {
                return null;
              }

              return (
                <div key={key}>
                  {parentMatches && (
                    <CommandItem onSelect={() => toggleParent(option)}>
                      <CheckboxIcon isSelected={selectedValues.some((val) => valueToString(val) === key)} />
                      <span>{option.label}</span>
                    </CommandItem>
                  )}
                  {matchingChildren.map((child) => (
                    <CommandItem
                      key={`${key}-${subValueToString(child.value)}`}
                      onSelect={() => toggleChild(child.value)}
                    >
                      <CheckboxIcon
                        isSelected={selectedSubValues.some(
                          (subVal) => subValueToString(subVal) === subValueToString(child.value),
                        )}
                      />
                      <span>{child.label}</span>
                    </CommandItem>
                  ))}
                </div>
              );
            }

            const selectedChildCount = children.filter(({ value }) =>
              selectedSubValues.some((subVal) => subValueToString(subVal) === subValueToString(value)),
            ).length;
            const allChildrenSelected = hasChildren && selectedChildCount === children.length;
            const someChildrenSelected = hasChildren && selectedChildCount > 0 && !allChildrenSelected;
            const isParentChecked = selectedValues.some((val) => valueToString(val) === key) || allChildrenSelected;

            return (
              <div
                key={key}
                ref={(el) => {
                  if (el) {
                    itemRefs.current[key] = el;
                  } else {
                    delete itemRefs.current[key];
                  }
                }}
                onMouseEnter={() => (hasChildren ? openSubmenu(option.value) : cancelClose())}
                onMouseLeave={() => hasChildren && scheduleClose()}
              >
                <CommandItem onSelect={() => toggleParent(option)}>
                  <CheckboxIcon isSelected={isParentChecked} isIndeterminate={someChildrenSelected} />
                  <span>{option.label}</span>
                  {hasChildren && someChildrenSelected && (
                    <Badge
                      variant="secondary"
                      className="ml-auto shrink-0 whitespace-nowrap rounded-sm px-1 font-normal"
                    >
                      {selectedChildCount} selected
                    </Badge>
                  )}
                  {hasChildren && <ChevronRight className="ml-auto h-4 w-4 shrink-0 text-muted-foreground" />}
                </CommandItem>
              </div>
            );
          })}
        </CommandGroup>
      </FilterDropdownBase>
      {expandedOption && submenuRect && (
        <SubmenuPortal
          rect={submenuRect}
          children={expandedOption.children ?? []}
          selectedSubValues={selectedSubValues}
          subValueToString={subValueToString}
          onToggleChild={toggleChild}
          onMouseEnter={handleSubmenuMouseEnter}
          onMouseLeave={handleSubmenuMouseLeave}
        />
      )}
    </>
  );
}
